import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select, { components } from 'react-select';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';

// Shared dropdown used by every filter on Maxes & Goals

const ChooserContainer = styled.div`
  position: relative;
  width: ${(props) => props.width};
  max-width: 100%;
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '40px',
    height: '40px',
    border: '1px solid #D6D6D6',
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
    backgroundColor: '#FFFFFF',
    '&:hover': { borderColor: '#D6D6D6' },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 16px',
    height: '38px',
    position: 'relative',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    border: '1px solid #EEEEEE',
    boxShadow: '0px 16px 24px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
    borderRadius: '4px',
    overflow: 'hidden',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: '360px',
  }),
  placeholder: (provided) => ({
    ...provided, margin: 0, color: '#9E9E9E', display: 'flex', alignItems: 'center', height: '100%',
  }),
  // Otherwise the typed search text shows larger than the rest
  input: (provided) => ({
    ...provided, margin: 0, fontFamily: '"Nunito Sans"', fontSize: '14px',
  }),
  clearIndicator: (provided, state) => (
    state.selectProps.isClearable ? { ...provided, padding: '0 4px', color: '#9E9E9E' } : { display: 'none' }
  ),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({ ...provided, color: '#9E9E9E' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, margin: 0, padding: 0 }),
};

const PlaceholderWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  height: 100%;
  color: #444444;
`;

const GroupHeader = styled.div`
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9E9E9E;
  padding: 8px 16px;
  background-color: #F9F9F9;
  border-bottom: 1px solid #EEEEEE;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 8px ${(props) => 16 + (props.level || 0) * 16}px;
  background-color: ${(props) => {
    if (props.isSelected) return '#FCFCFC';
    return props.isFocused ? '#F5F5F5' : '#FFFFFF';
  }};
  color: #1A1A1A;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 14px;
`;

const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Sub-calendars are indented with an arrow
const CustomOption = (props) => {
  const {
    children, isSelected, isFocused, innerRef, innerProps, data,
  } = props;
  const level = data.level || 0;
  return (
    <OptionRow
      ref={innerRef}
      {...innerProps}
      isSelected={isSelected}
      isFocused={isFocused}
      level={level}
    >
      {level > 0 && <IcomoonReact iconSet={iconSet} icon='sub-arrow' size={12} color='#9E9E9E' />}
      <OptionLabel>{children}</OptionLabel>
    </OptionRow>
  );
};

CustomOption.propTypes = {
  children: PropTypes.node,
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool,
  innerRef: PropTypes.func,
  innerProps: PropTypes.shape({}),
  data: PropTypes.shape({ level: PropTypes.number }),
};

CustomOption.defaultProps = {
  children: null,
  isSelected: false,
  isFocused: false,
  innerRef: undefined,
  innerProps: undefined,
  data: {},
};

const CustomGroupHeading = ({ children, innerProps }) => (
  <GroupHeader {...innerProps}>{children}</GroupHeader>
);

CustomGroupHeading.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({}),
};

CustomGroupHeading.defaultProps = {
  children: null,
  innerProps: {},
};

// Extra props like icon arrive through selectProps
const CustomSingleValue = (props) => {
  const { children, data, selectProps } = props;
  return (
    <components.SingleValue {...props}>
      <PlaceholderWithIcon>
        <IcomoonReact iconSet={iconSet} icon={data.icon || selectProps.icon} size={18} color='#1A1A1A' />
        {children}
      </PlaceholderWithIcon>
    </components.SingleValue>
  );
};

CustomSingleValue.propTypes = {
  children: PropTypes.node,
  data: PropTypes.shape({ icon: PropTypes.string }),
  selectProps: PropTypes.shape({ icon: PropTypes.string }).isRequired,
};
CustomSingleValue.defaultProps = { children: null, data: {} };

const CustomSingleValueWithMenu = (props) => {
  const { selectProps } = props;
  if (selectProps.menuIsOpen) return null;
  return <CustomSingleValue {...props} />;
};

CustomSingleValueWithMenu.propTypes = {
  selectProps: PropTypes.shape({ menuIsOpen: PropTypes.bool }).isRequired,
};

const CustomPlaceholder = (props) => {
  const { selectProps } = props;
  if (selectProps.menuIsOpen) return null;
  return (
    <components.Placeholder {...props}>
      <PlaceholderWithIcon>
        <IcomoonReact iconSet={iconSet} icon={selectProps.icon} size={18} color='#1A1A1A' />
        {selectProps.placeholderText}
      </PlaceholderWithIcon>
    </components.Placeholder>
  );
};

CustomPlaceholder.propTypes = {
  selectProps: PropTypes.shape({
    menuIsOpen: PropTypes.bool,
    placeholderText: PropTypes.string,
    icon: PropTypes.string,
  }).isRequired,
};

const SearchOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding-left: 16px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 400;
  color: #9E9E9E;
  pointer-events: none;
  z-index: 1;
`;

const CustomValueContainer = (props) => {
  const { children, selectProps } = props;
  const showOverlay = selectProps.menuIsOpen && !selectProps.inputValue;
  return (
    <components.ValueContainer {...props}>
      {showOverlay && (
        <SearchOverlay>
          <IcomoonReact iconSet={iconSet} icon={selectProps.icon} size={18} color='#1A1A1A' />
          <span style={{
            marginLeft: '8px', fontFamily: '"Nunito Sans"', fontSize: '14px', fontWeight: 400,
          }}
          >
            {selectProps.searchOverlayText}
          </span>
        </SearchOverlay>
      )}
      {children}
    </components.ValueContainer>
  );
};

CustomValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    inputValue: PropTypes.string,
    menuIsOpen: PropTypes.bool,
    icon: PropTypes.string,
    searchOverlayText: PropTypes.string,
  }).isRequired,
};

CustomValueContainer.defaultProps = { children: null };

const SELECT_COMPONENTS = {
  Option: CustomOption,
  GroupHeading: CustomGroupHeading,
  SingleValue: CustomSingleValueWithMenu,
  Placeholder: CustomPlaceholder,
  ValueContainer: CustomValueContainer,
};

const FilterSelect = ({
  inputId, ariaLabel, icon, options, value, onChange, isLoading,
  placeholder, searchOverlayText, noOptionsMessage, width, isClearable,
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const placeholderText = isLoading ? 'Loading...' : placeholder;

  return (
    <ChooserContainer width={width}>
      <SelectWrapper>
        <Select
          inputId={inputId}
          aria-label={ariaLabel}
          styles={customSelectStyles}
          value={value}
          onChange={onChange}
          options={options}
          isLoading={isLoading}
          isSearchable
          isClearable={isClearable}
          placeholder={placeholderText}
          placeholderText={placeholderText}
          icon={icon}
          searchOverlayText={searchOverlayText}
          hideSelectedOptions={false}
          menuIsOpen={menuIsOpen}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          components={SELECT_COMPONENTS}
          classNamePrefix='react-select'
          filterOption={(option, searchText) => (
            !searchText || option.label.toLowerCase().includes(searchText.toLowerCase())
          )}
          noOptionsMessage={() => noOptionsMessage}
        />
      </SelectWrapper>
    </ChooserContainer>
  );
};

FilterSelect.propTypes = {
  inputId: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: PropTypes.shape({ label: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  searchOverlayText: PropTypes.string.isRequired,
  noOptionsMessage: PropTypes.string,
  width: PropTypes.string,
  isClearable: PropTypes.bool,
};

FilterSelect.defaultProps = {
  value: null,
  isLoading: false,
  noOptionsMessage: 'No matches found',
  width: '420px',
  isClearable: false,
};

export default FilterSelect;
